import React from 'react';
import { motion } from 'framer-motion';
import AppGrid from './AppGrid';
import './MainContent.css';

// MainContent Component - Main dashboard area with app sections
// Displays searchable app tiles organized by categories
// ARCHITECTURE: SSO dashboard only - modules open in new tabs with auth tokens
// Animation: Content fades in and tiles cascade in

interface App {
  name: string;
  type: string;
  logo: string;
  status: string;
  category: string;
  moduleUrl?: string; // URL for external module (e.g., http://localhost:3004)
}

interface Section {
  title: string;
  subtitle: string;
  apps: App[];
}

interface MainContentProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

const MainContent: React.FC<MainContentProps> = ({ searchQuery, onSearchChange }) => {
  const contentVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.6, delay: 0.3 }
    }
  };

  const titleVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, delay: 0.4 }
    }
  };

  const searchVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, delay: 0.5 }
    }
  };

  // App data for all sections - organized by category
  // TODO: Connect to backend API to fetch dynamic app list
  const appSections: Section[] = [
    {
      title: '💼 Core Services',
      subtitle: 'Employee Data Management',
      apps: [
        {
          name: '📋 Attendance',
          type: 'Employee Attendance Tracking',
          logo: '📋',
          status: 'Active',
          category: 'core',
          moduleUrl: 'http://localhost:3004'
        },
        {
          name: 'Employee Core',
          type: 'Employee Data Management',
          logo: '👤',
          status: 'Active',
          category: 'core'
        },
        {
          name: 'HRMS',
          type: 'Human Resource Management',
          logo: '📋',
          status: 'Active',
          category: 'core'
        },
        {
          name: 'Contact Centre',
          type: 'Customer Services Hub',
          logo: '☎️',
          status: 'Active',
          category: 'core'
        },
        {
          name: 'QA Module',
          type: 'Quality Assurance',
          logo: '✓',
          status: 'Active',
          category: 'core'
        }
      ]
    },
    {
      title: '📊 Data & Intelligence',
      subtitle: 'Analytics and Security',
      apps: [
        {
          name: 'Big Data Module',
          type: 'Centralized Statistics & Analytics',
          logo: '📈',
          status: 'Active',
          category: 'data'
        },
        {
          name: 'Security & Surveillance',
          type: 'System Monitoring & Protection',
          logo: '🛡️',
          status: 'Active',
          category: 'data'
        }
      ]
    },
    {
      title: '🎯 Dashboards & Views',
      subtitle: 'Role-Based Dashboard Instances',
      apps: [
        {
          name: 'Real-time Dashboard',
          type: 'Live System Monitoring',
          logo: '📊',
          status: 'Active',
          category: 'dashboard'
        },
        {
          name: 'Analyst Instance',
          type: 'Role-Based Analytics View',
          logo: '👥',
          status: 'Active',
          category: 'dashboard'
        },
        {
          name: 'Team Leader Instance',
          type: 'Leadership Dashboard',
          logo: '🎖️',
          status: 'Active',
          category: 'dashboard'
        },
        {
          name: 'Admin Dashboard',
          type: 'System Administration',
          logo: '⚙️',
          status: 'Requires Approval',
          category: 'dashboard'
        }
      ]
    },
    {
      title: '🔗 Integrations',
      subtitle: 'Authentication & Communication',
      apps: [
        {
          name: '2FA / SSO',
          type: 'Authentication Service',
          logo: '🔐',
          status: 'Active',
          category: 'integration'
        },
        {
          name: 'API Gateway',
          type: 'Service Communication Layer',
          logo: '🌐',
          status: 'Active',
          category: 'integration'
        }
      ]
    }
  ];

  return (
    <motion.main
      className="main-content"
      variants={contentVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header Section */}
      <div className="content-header">
        <motion.div variants={titleVariants}>
          <h1 className="content-title">My Apps</h1>
          <p className="content-subtitle">
            Access all services in the Antasis Ecosystem and integrated applications
          </p>
        </motion.div>
      </div>

      {/* Search Section */}
      <motion.div className="search-container" variants={searchVariants}>
        <input
          type="text"
          className="search-box"
          placeholder="🔍 Search your apps"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </motion.div>

      {/* App Sections Grid */}
      <div className="app-sections">
        {appSections.map((section, idx) => (
          <AppGrid key={idx} section={section} index={idx} />
        ))}
      </div>

      {/* Footer removed — branding and footer moved into sidebar or omitted */}
    </motion.main>
  );
};

export default MainContent;
